import type { ParticipantQr } from '@piccolohealth/pbs-common';
import { ChakraV3, FancyDate, FloatingPopover, FloatingPopoverBody } from '@piccolohealth/ui';
import { DateTime, inflection, P } from '@piccolohealth/util';
import React from 'react';
import { FaInfoCircle } from 'react-icons/fa';
import { UserDescription } from '../../components/user/UserDescription';

interface Props {
  qr: ParticipantQr;
}

export const ParticipantQRRecordingsPopover = (props: Props) => {
  const { qr } = props;

  const recordingsByUser = React.useMemo(() => {
    return P.mapValues(
      P.groupBy(
        qr.behaviours.flatMap((behaviour) => behaviour.recordings),
        (recording) => recording.user.email,
      ),
      (recordings) => recordings?.length ?? 0,
    );
  }, [qr.behaviours]);

  const lastUsed = React.useMemo(() => {
    const recordings = qr.behaviours.flatMap((behaviour) => behaviour.recordings);
    const latestRecording = P.maxBy(recordings, (recording) =>
      DateTime.fromISO(recording.createdAt as unknown as string).toUnixInteger(),
    );
    return latestRecording?.createdAt;
  }, [qr]);

  if (!lastUsed) {
    return <ChakraV3.Text>Not used yet</ChakraV3.Text>;
  }

  return (
    <ChakraV3.HStack gap={1}>
      Last used:
      <FancyDate
        date={lastUsed}
        format={DateTime.DATE_SHORT}
        direction='row'
        showHumanized={false}
      />
      <FloatingPopover
        render={() => (
          <FloatingPopoverBody>
            <ChakraV3.Text fontWeight='semibold'>
              QR has been used by {qr.users.length} {inflection.inflect('user', qr.users.length)}
            </ChakraV3.Text>

            {!P.isEmpty(qr.users) && (
              <ChakraV3.Stack>
                {qr.users.map((user) => {
                  const userRecording = recordingsByUser[user.email];

                  if (P.isUndefined(userRecording)) {
                    return null;
                  }

                  return (
                    <ChakraV3.HStack key={user.email} gap={7} align='center' flexShrink={0}>
                      <UserDescription name={user.name} secondary={user.email} />
                      <ChakraV3.Spacer />
                      {recordingsByUser[user.email]}{' '}
                      {inflection.inflect('recording', userRecording)}
                    </ChakraV3.HStack>
                  );
                })}
              </ChakraV3.Stack>
            )}
          </FloatingPopoverBody>
        )}
        trigger='hover'
        placement='bottom'
      >
        <ChakraV3.IconButton size='sm' aria-label='User recording details' variant='plain'>
          <FaInfoCircle />
        </ChakraV3.IconButton>
      </FloatingPopover>
    </ChakraV3.HStack>
  );
};
